import React from 'react';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import {
  Dropdown as AntDDropdown,
  Menu,
  MenuProps,
  DropDownProps as AntDDropDownProps,
} from 'antd';

import { Button, ButtonProps } from '..';
import { getScrollParent } from '../utils';

import './index.less';

export interface Props extends Partial<AntDDropDownProps> {
  variant?: ButtonProps['variant'];
  contents: MenuProps['items'];
  id: string;
  buttonProps?: Partial<ButtonProps>;
  menuProps?: Partial<MenuProps>;
  children?: React.ReactNode;
  dropdownRef?(node: HTMLElement): void;
  onMenuItemClick?: MenuProps['onClick'];
  isFloatingMenu?: boolean;
}

export type { Props as DropdownProps };

export default function Dropdown({
  children,
  contents,
  id,
  dropdownRef,
  onMenuItemClick,
  onVisibleChange,
  className,
  visible: defaultVisible,
  variant = 'outlined',
  menuProps = {},
  buttonProps = {},
  isFloatingMenu = false,
  ...rest
}: Props): JSX.Element {
  const [visible, setVisible] = React.useState(defaultVisible);
  const { className: buttonClassName, ...btnProps } = buttonProps || {};
  const { className: menuClassName } = menuProps || {};

  React.useEffect(() => {
    if (!isNil(defaultVisible)) {
      setVisible(defaultVisible);
    }
  }, [defaultVisible]);

  return (
    <AntDDropdown
      className={classNames('sl-dropdown', className)}
      trigger={['click']}
      overlay={
        <Menu
          theme="light"
          id={id}
          className={classNames(
            'tw-overflow-hidden',
            {
              'tw-border tw-py-0 tw-border-gray-300': visible,
              'tw-border-t-0 tw-rounded-t-none': variant !== 'text' && !isFloatingMenu,
              'tw-mt-1 tw-list-none tw-px-1': !isFloatingMenu,
            },
            menuClassName
          )}
          ref={dropdownRef as unknown as React.LegacyRef<Menu>}
          onClick={e => {
            setVisible(false);
            e.domEvent.stopPropagation();
            onMenuItemClick && onMenuItemClick(e);
          }}
          items={contents}
          {...menuProps}
        />
      }
      onVisibleChange={visible => {
        setVisible(visible);
        onVisibleChange && onVisibleChange(visible);
      }}
      visible={visible}
      getPopupContainer={triggerNode => getScrollParent(triggerNode)}
      {...rest}
    >
      <Button
        id={`${id}-toggle`}
        className={classNames(
          {
            'tw-border-b-0 tw-rounded-b-none': visible && !isFloatingMenu,
            'tw-border-gray-300': variant !== 'text',
          },
          buttonClassName
        )}
        variant={variant}
        {...btnProps}
        onClick={e => {
          e.stopPropagation();
          btnProps.onClick && btnProps.onClick(e);
        }}
      >
        {children}
      </Button>
    </AntDDropdown>
  );
}
